import { Controller } from '@hotwired/stimulus'
import Uploader from '../src/uploader'
import { setFlash } from '../src/helpers'

const DEFAULT_LEAVING_MESSAGE = 'You have uploads in progress. Are you sure you want to exit this page?'

export default class extends Controller {
  static targets = ['fileField']
  static values = {
    maxDimension: { type: Number, default: 3840 },
    maxBytes: { type: Number, default: 52428800 }, // 50mb
    whitelist: { type: Array, default: ['image/jpeg', 'image/png', 'application/pdf'] },
    attachmentName: { type: String, default: 'file' },
    uploading: { type: Boolean, default: false },
    leavingMessage: { type: String, default: DEFAULT_LEAVING_MESSAGE }
  }

  selectFiles() {
    this.fileFieldTarget.click()
  }

  async handleNewFiles(event) {
    const files = Array.from(event.target.files)
    const approvedFiles = await Promise.all(files.map(file => this.validateFile(file)))
    const results = approvedFiles.filter(Boolean).map(f => f)

    this.fileFieldTarget.value = null

    results.forEach(file => this.submitFile(file))
  }

  async validateFile(file) {
    if (!this.whitelistValue.includes(file.type)) {
      this.dispatch('invalid-type', { detail: { file: file } })
      setFlash('alert', `Invalid file type: ${file.name}`)
      return false
    }

    if (file.size > this.maxBytesValue) {
      this.dispatch('over-size', { detail: { file: file } })
      setFlash('alert', `File size too large: ${file.name}`)
      return false
    }

    if (this.hasMaxDimValue) {
      const largestDimension = await this.getLargestDimension(file)

      if (largestDimension > this.maxDimensionValue) {
        this.dispatch('over-dimension', { detail: { file: file } })
        setFlash('alert', `Dimensions are too lage: ${file.name}`)
        return false
      }
    }

    return file
  }

  getLargestDimension(file) {
    return new Promise(resolve => {
      const img = new Image()
      img.src = URL.createObjectURL(file)

      img.onload = () => {
        const largestDimension = Math.max(img.width, img.height)
        URL.revokeObjectURL(img.src)
        resolve(largestDimension)
      }
    })
  }

  submitFile(file) {
    this.dispatch('submit', { detail: { file: file } })
  }

  uploadFile({ detail: { element, file } }) {
    const progressHtml = `
      <div class="upload-progress absolute inset-0 flex items-center justify-center overflow-hidden bg-theme-gray/25 background-blur-sm">
        <div 
          class="progress-bar absolute inset-0 bg-theme-gray/75 origin-left transition-transform duration-200 ease-in-out animate-pulse" 
          style="transform: scaleX(0)"></div>
      </div>
    `
    element.insertAdjacentHTML('beforeend', progressHtml)
    element.querySelector('.filename').innerText = file.name

    if (element.querySelector('img')) {
      element.querySelector('img').src = URL.createObjectURL(file)
    }

    const uploader = new Uploader(file, {
      id: element.dataset.tempId,
      onChangeFile: this.handleFileProgress
    })

    this.uploadingValue = true
    uploader.start()
  }

  handleFileProgress = event => {
    const attachment = document.querySelector(`[data-temp-id="${event.id}"]`)

    switch (event.state) {
      case 'uploading':
        attachment.querySelector('.progress-bar').style.transform = `scaleX(${event.progress / 100})`
        break
      case 'error':
        attachment.remove()
        setFlash('alert', `Error uploading ${event.file.name}.`)
        break
      case 'finished': {
        attachment.querySelector('.upload-progress').remove()

        const hiddenSignedIdField = document.createElement('input')

        hiddenSignedIdField.setAttribute('type', 'hidden')
        hiddenSignedIdField.setAttribute('value', event.signedId)
        hiddenSignedIdField.name = attachment.querySelector("input[name*='_destroy']").name.replace(/_destroy/g, this.attachmentNameValue)

        attachment.appendChild(hiddenSignedIdField)

        this.uploadingValue = false
        break
      }
      default:
        break
    }
  }

  guardLeave(event) {
    if (this.uploadingValue) {
      if (event.type === 'turbo:before-visit') {
        if (!window.confirm(this.leavingMessageValue)) {
          event.preventDefault()
        }
      } else {
        event.returnValue = this.leavingMessageValue
        return event.returnValue
      }
    }
  }
}
